import React, { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdClose } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Flex,
  Button,
  Select,
  IconButton,
  Checkbox,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, ComeBackHeader, Container, SimpleInput } from 'components';
import { colors } from 'theme/components/colors';
import { isValidCPF } from 'utils/validations';
import * as z from 'zod';
import { areas, bases } from '../mocks';

const UserSchema = z.object({
  name: z.string().nonempty('Insira um nome válido'),
  email: z
    .string()
    .email('E-mail inválido')
    .nonempty('E-mail não pode ser vazio'),
  cpf: z
    .string()
    .min(14, { message: 'CPF não pode ser vazio' })
    .nonempty('CPF não pode ser vazio')
    .refine(value => !value.includes('_'), 'CPF vazio ou incompleto')
    .refine(value => isValidCPF(value), 'CPF inválido'),
  role: z.string().nonempty('Insira um cargo válido'),
  pw: z.string().optional(),
  bases: z.string().array().nonempty(),
  permissions: z.string().array().nonempty(),
});

interface UserSchemaProps {
  name: string;
  email: string;
  cpf: string;
  role: string;
  pw?: string;
  bases: string[];
  permissions: string[];
}

export const AccessUser: React.FC = () => {
  const [isADM, setIsAdm] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<UserSchemaProps>({
    resolver: zodResolver(UserSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const groups = watch('bases') || [];
  const permissions = watch('permissions') || [];

  const handleGroups = useCallback(
    (teste: string, onChange?: (param: string[]) => void) => {
      if (!onChange) {
        return setValue(
          'bases',
          groups.filter(group => group !== teste),
        );
      }
      if (groups.includes(teste)) {
        onChange(groups.filter(group => group !== teste));
      } else onChange([...groups, teste]);
    },
    [groups],
  );

  const handlePermissions = useCallback(
    (name: string, onChange: (param: string[]) => void) => {
      if (permissions.includes(name)) {
        onChange(permissions.filter(permission => permission !== name));
      } else onChange([...permissions, name]);
    },
    [permissions],
  );

  const onSubmit = useCallback(async (data: UserSchemaProps) => {
    // eslint-disable-next-line no-console
    console.log(data);
  }, []);

  return (
    <Container>
      <ComeBackHeader
        actionText="Voltar"
        navigateTo={() => navigate('/acessos')}
        title={
          isADM ? 'Criar cadastro do cliente' : 'Criar cadastro do usuário'
        }
      />

      <Card py="32px" px="18px">
        <Flex
          flexDir={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
        >
          <Flex flexDir="column" gap="24px" w={{ base: '100%', md: '48%' }}>
            <Text fontSize="18px" textColor="text.100">
              Dados de cadastro
            </Text>
            <Text fontSize="12px" mb="16px" mt="-20px" textColor="text.100">
              Insira os dados de cadastro do cliente
            </Text>
            <SimpleInput
              {...register('name')}
              error={errors.name?.message}
              size="lg"
              labelColor="text.100"
              bg="transparent"
              borderColor="grey.500"
              color="text.100"
              fontSize="14px"
              fontWeight="500"
              _placeholder={{ textColor: 'text.100' }}
              label="*Nome completo"
              placeholder="Insira o nome do cliente"
            />
            <SimpleInput
              {...register('email')}
              error={errors.email?.message}
              type="email"
              size="lg"
              bg="transparent"
              borderColor="grey.500"
              color="text.100"
              fontSize="14px"
              fontWeight="500"
              _placeholder={{ textColor: 'text.100' }}
              placeholder="E-mail"
              labelColor="text.100"
              label="*E-mail"
            />
            <SimpleInput
              {...register('cpf')}
              error={errors.cpf?.message}
              type="cpf"
              size="lg"
              bg="transparent"
              borderColor="grey.500"
              color="text.100"
              fontSize="14px"
              fontWeight="500"
              _placeholder={{ textColor: 'text.100' }}
              placeholder="XXX.XXX.XXX-XX"
              labelColor="text.100"
              label="*CPF"
            />
            <SimpleInput
              {...register('role')}
              error={errors.role?.message}
              size="lg"
              bg="transparent"
              borderColor="grey.500"
              color="text.100"
              fontSize="14px"
              fontWeight="500"
              _placeholder={{ textColor: 'text.100' }}
              placeholder="Cargo"
              labelColor="text.100"
              label="*Cargo"
            />
            <SimpleInput
              {...register('pw')}
              error={errors.pw?.message}
              size="lg"
              bg="transparent"
              borderColor="grey.500"
              color="text.100"
              fontSize="14px"
              fontWeight="500"
              _placeholder={{ textColor: 'text.100' }}
              placeholder="Senha"
              labelColor="text.100"
              label="Senha provisória"
            />
          </Flex>
          <Flex
            flexDir="column"
            mt={{ base: '24px', md: '88px' }}
            w={{ base: '100%', md: '48%' }}
          >
            {isADM ? (
              <>
                <Text fontSize="sm" mb="4px" textColor="text.100">
                  Bases
                </Text>

                <Controller
                  control={control}
                  name="bases"
                  render={({ field: { onChange } }) => (
                    <Select
                      bg="transparent"
                      borderColor="grey.500"
                      color="text.100"
                      fontSize="14px"
                      fontWeight="500"
                      size="lg"
                      placeholder="Escolha a base"
                      value="Escolha a base"
                      onChange={value =>
                        handleGroups(value.target.value, onChange)
                      }
                    >
                      {bases.map(pageSize => (
                        <option key={pageSize} value={pageSize}>
                          {pageSize}
                        </option>
                      ))}
                    </Select>
                  )}
                />

                {errors.bases?.message && (
                  <Text fontSize="sm" mt="4px" textColor="red.200">
                    Selecione no mínimo uma base
                  </Text>
                )}
                {groups.length && (
                  <Text mt="22px" textColor="text.100">
                    Bases selecionadas
                  </Text>
                )}
                <Flex flexWrap="wrap" width="100%">
                  {groups.map(item => (
                    <Flex
                      mr="8px"
                      key={item}
                      mt="8px"
                      borderRadius="6px"
                      alignSelf="flex-start"
                      alignItems="center"
                      border="1px solid"
                      borderColor="gray.300"
                      pr="8px"
                      py="8px"
                    >
                      <IconButton
                        onClick={() => handleGroups(item)}
                        h="0px"
                        w="0px"
                        aria-label="Excluir grupo"
                        icon={<MdClose size="16px" color={colors.gray[100]} />}
                      />
                      <Text fontSize="14px" ml="-8px" textColor="text.100">
                        {item}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </>
            ) : (
              <Flex flexDir="column" gap="16px">
                <Text mt="0px" textColor="text.100">
                  Áreas de acesso
                </Text>
                <Controller
                  control={control}
                  name="permissions"
                  render={({ field: { onChange } }) => (
                    <>
                      {areas.map(item => (
                        <Checkbox
                          checked={permissions.includes(item)}
                          onChange={() => handlePermissions(item, onChange)}
                          key={item}
                          borderColor="grey.300"
                          color="text.100"
                        >
                          {item}
                        </Checkbox>
                      ))}
                    </>
                  )}
                />
                {errors.permissions?.message && (
                  <Text fontSize="sm" textColor="red.200">
                    Selecione no mínimo uma
                  </Text>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
        <Button
          onClick={handleSubmit(onSubmit)}
          mt="40px"
          w={{ base: '100%', md: '120px' }}
          alignSelf={{ base: 'center', md: 'end' }}
        >
          Concluir
        </Button>
      </Card>
    </Container>
  );
};
